import React, { useEffect } from "react";
import { useHistory } from "react-router";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import DoneIcon from "@material-ui/icons/Done";
import clsx from "clsx";
import {
  Button,
  makeStyles,
  Typography,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Theme,
} from "@material-ui/core";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import CustomDialog from "@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog";
import { first, flatten, get, intersectionWith, isEmpty, isEqual, values } from "lodash";
import { BasketItem, Category } from "../../Types/Types";

export const useStyles = makeStyles((theme: Theme) => ({
  selectorDialog: {
    maxWidth: 600,
    margin: "auto",
  },
  actions: {
    display: "flex",
    marginTop: 24,
    alignItems: "center",
    justifyContent: "space-between",
  },
  category: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    "& svg": {
      color: "#4c4c4c",
    }
  },
  activeButton: {
    width: 180,
    margin: "10px 0px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    [theme.breakpoints.down('xs')]: {
      width: 140,
    }
  },
  categoryTitle: {
    fontSize: "20px !important",
    paddingRight: 6,
    color: "#BCBCBC",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  completedCategory:{
    pointerEvents: 'none',
    "& svg": {
      color: "#BCBCBC"
    }
  },
  nextStep: {
    fontSize: "24px !important",
    color: "#4c4c4c",
  }
}));

export interface CategorySelectorDialogProps {
  show: boolean;
  onClose: () => void;
  categories: Category[];
  isLoggedIn: boolean;
  fetchCategories: () => void;
  setSelectedCategoryId: (id: number) => void;
  basketItems: BasketItem[]
}

const CategorySelectorDialog = (props: CategorySelectorDialogProps) => {
  const classes = useStyles();
  const history = useHistory();
  const {show, onClose, categories, isLoggedIn, fetchCategories, setSelectedCategoryId } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  useEffect(()=>{
    if(!categories || isEmpty(categories)) {
      fetchCategories();
    }
  },[]);

  const hasItemInBasket = (category: Category) => {
    const items = flatten(category.items.map(i => i.subCategories && i.subCategories.length > 0 ? i.subCategories : i));
    const identifiers = items.map(s => s.identifier);
    const basketIdentifers = props.basketItems.map(s => s.configuration.item.identifier);
    return intersectionWith(identifiers, basketIdentifers, isEqual).length > 0;
  }

  const jumpToCategory = (categoryId: number) => {
    onClose();
    history.push({pathname: '/products/overview', search: window.location.search})
    setSelectedCategoryId(categoryId);
  }

  const basket = props.basketItems.map(s => s.configuration.item.identifier);
  const basketCategories = basket.map(id => {
    let identifier = null;
    values(categories).some(category => {
      const items = flatten(category.items.map(i => i.subCategories && i.subCategories.length > 0 ? i.subCategories : i));
      const identifiers = items.map(s => s.identifier);
      if(identifiers.includes(id)) {
        identifier = category.id;
        return true;
      }
    })
    return identifier;
  })
  const categoryIds: number[] = values(categories).map(category => category.id);
  const nextCategory: number = first(categoryIds.filter(val => !basketCategories.includes(val)));

  return (
    <CustomDialog
      open={show}
      className={classes.selectorDialog}
      onCancel={onClose}
      dialogTitle={t("categorySelectorDialog.title")}
      fullScreen={isSmallScreen}
    >
      <DialogContent>
        <Typography classes={{ root: classes.infoText }} variant="body1">
          {t("categorySelectorDialog.info")}
        </Typography>
       {values(categories).map((category, i) => (
          <div className={clsx(classes.category, get(hasItemInBasket(category), "categoryId") === category.id && classes.completedCategory)}
            onClick={() => jumpToCategory(category.id)}
          >
            <ArrowRightIcon />
            <Typography
              className={clsx(classes.categoryTitle, nextCategory === category.id && classes.nextStep)}
            > 
              {(i + 1) + ". " + t("categorySelectorDialog." + category.category)}
            </Typography>
            {hasItemInBasket(category) && <DoneIcon  style={{ color: "#447bb9" }} />}
          </div>
       ))}
      </DialogContent>
      <DialogActions className={classes.actions}>
          <Button
            variant="contained"
            className={classes.activeButton}
            onClick={() => {
              nextCategory ? jumpToCategory(nextCategory) : jumpToCategory(get(categories[0], "id"));
            }}
            >
            {t("categorySelectorDialog.toNextStep")}
          </Button>
          <Button
            variant="contained"
            className={classes.activeButton}
            onClick={() => {
              history.push({pathname: 'basket/address', search: window.location.search});
              onClose();
            }}
          >
            {isLoggedIn ? t("categorySelectorDialog.toBasket") : t("categorySelectorDialog.toRequestList")}
          </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default CategorySelectorDialog